import { Component, Inject, LOCALE_ID, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppConstants } from "src/app/constants/constants";
import { getValueFromQueryParams } from "src/app/shared/url-functions";
import { Course } from "src/app/_models/Course";
import { LearnerCourseLogService } from "src/app/_services/learner-course-log.service";
import { LearnerCourseService } from "src/app/_services/learner-course.service";
import { UserSessionService } from "src/app/_services/user-session.service";
import { Userprofile } from "src/app/_models/Userprofile";
import { Store } from "@ngrx/store";
import { selectOwnUserProfile } from "src/app/_store/user-profile/user-profile.selectors";
import { GetLearnerCourseScreenerResult } from "src/app/_store/learner-course/learner-course.actions";
import { selectLearnerCourseScreenerResult } from "src/app/_store/learner-course/learner-course.selectors";
import { PublicUserSessionService } from "src/app/_services/public-user-session.service";
import { Modes } from "src/app/constants/modes";
import { Screener } from "src/app/_models/Screener";
import { ScreenerService } from "src/app/_services/screener.service";
import { CourseContentMetadata } from "src/app/_models/CourseContentMetadata";

@Component({
  selector: "app-courses",
  templateUrl: "./courses.component.html",
  styleUrls: ["./courses.component.scss"],
})
export class CoursesComponent implements OnInit {
  public courses: Course[];
  courseListStatus: string[];
  siteLanguageCode: string;
  hasAssignedCourses: boolean = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  ready: boolean = false;
  currentUser: Userprofile;
  siteLanguage: string;
  isScreenerResult = false;
  constructor(
    private router: Router,
    private learnerCourseService: LearnerCourseService,
    private learnerCourseLogService: LearnerCourseLogService,
    private userSessionService: UserSessionService,
    @Inject(LOCALE_ID) protected localeId: string,
    private publicUserSessionService: PublicUserSessionService,
    private screenerService: ScreenerService,
    private store: Store,
  ) {
    this.siteLanguageCode = localeId;
    
  }

  ngOnInit(): void {
    this.initStoreSelectors();
    this.getLearnerCoursesByLanguage();

    this.siteLanguage = this.siteLanguageCode != AppConstants.WELSH_LANG_CODE ? null : this.siteLanguageCode;
  }

  initStoreSelectors(): void {
    this.store.select(selectOwnUserProfile)
    .pipe(takeUntil(this.destroy$))
    .subscribe((result) => {
      if(result)
        this.currentUser = result;
    });

    this.store.select(selectLearnerCourseScreenerResult)
      .pipe(takeUntil(this.destroy$)) 
      .subscribe((result: Course[]) => {
      this.hasAssignedCourses = false;
      this.courses = result;
      this.ready = true;
      this.userSessionService.onStartUpDisplayLoader$.next(false);
    });
  }

  public getLearnerCoursesByLanguage(): void {
    this.learnerCourseService
      .getLearnerCoursesByLanguage$(this.siteLanguageCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((courses) => {
        if (courses.length == 0) {
          this.store.dispatch(GetLearnerCourseScreenerResult({langId: this.siteLanguage, langCode: this.siteLanguageCode}));
        } else {
          this.hasAssignedCourses = true;
          this.learnerCourseLogService
            .getStartedCourses$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((courseIds) => {
              courses = courses.map((c) => ({
                ...c,
                started: courseIds.some((cs) => cs === c.id),
                name: c.name.replace(AppConstants.SECTION_DELIM, AppConstants.SECTION_DELIM_UNICODE)
              }));
              this.courses = courses;
              this.ready = true;
              this.userSessionService.onStartUpDisplayLoader$.next(false);
            });
        }
      });
  }

  //set index to zero when navigating to other courses
  maintainCourseTab(courseId: number): void {
    let prevUrl = sessionStorage.getItem(AppConstants.PREV_PATH);
    let prevCourse: number = Number(
      getValueFromQueryParams(prevUrl, AppConstants.COURSE_ID)
    );

    if (courseId != prevCourse) {
      sessionStorage.setItem(AppConstants.MAT_EXPANSION, null);
      localStorage.setItem(AppConstants.QA_TAB_INDEX, AppConstants.ZERO);
    }
  }

  public navigateToCourse(courseId: number, isNewDigLit: boolean = false, courseIconPath: string = ""): void {
    this.maintainCourseTab(courseId);

    this.router.navigate([AppConstants.LEARNER_COURSE_PAGE], {
      /**Use this LEARNER param for LearnerId instead of LEARNER_ID
       * because visited object will not update in External-Runtime
       * because it will not pass the updateLearnerObject service
       * due to the condition of having a LEARNER_ID param
       */      
      queryParams: { 
        learner: this.currentUser?.id, 
        courseId: courseId,
        isNewDigLit: isNewDigLit,
        courseIconPath: courseIconPath
      },
    });
  }

  public trackByFunction(item): void {
    if (!item) return null;
    return item.our_item_id;
  }

  public saveScreenerDetails(metadata: CourseContentMetadata[]): void {

    if(!metadata || metadata.length == 0) {
      return;
    }

    const objectId = metadata[0].value;
    
    if (
      this.sessionStorageHasValue() &&
      this.currentUser.firstName == sessionStorage.getItem(AppConstants.FIRST_NAME) &&
      this.currentUser.lastName == sessionStorage.getItem(AppConstants.LAST_NAME) &&
      this.currentUser.email == sessionStorage.getItem(AppConstants.EMAIL) &&
      sessionStorage.getItem(AppConstants.SCREENER_ID) != undefined
    ) {
      this.routeToContentPlayer(objectId);
      return;
    }

    sessionStorage.setItem(AppConstants.FIRST_NAME, this.currentUser.firstName);
    sessionStorage.setItem(AppConstants.LAST_NAME, this.currentUser.lastName);
    sessionStorage.setItem(AppConstants.EMAIL, this.currentUser.email);

    let screener = new Screener();
    screener.firstName = this.currentUser.firstName;
    screener.lastName = this.currentUser.lastName;
    screener.email = this.currentUser.email;
    screener.dateCreated = new Date();

    this.screenerService.addScreener$(screener).subscribe(
      (resp) => {
        sessionStorage.setItem(AppConstants.SCREENER_ID, String(resp.id));
        this.routeToContentPlayer(objectId);
      },
      () => {
        console.error(AppConstants.ERROR_ADDING_SCREENER_RECORD);
      }
    );
  }

  private sessionStorageHasValue(): boolean {
    return Boolean(
      sessionStorage.getItem(AppConstants.FIRST_NAME) != null &&
      sessionStorage.getItem(AppConstants.LAST_NAME) != null &&
      sessionStorage.getItem(AppConstants.EMAIL)
    );
  }

  private async routeToContentPlayer(objectId: string): Promise<void> {
    await firstValueFrom(this.publicUserSessionService.setIsAllowAccessContent$(AppConstants.TRUE));
      this.router.navigate([`/contentplayer/${objectId}`], {
        queryParams: { mode: Modes.Screener },
    });
  }
  
  
  ngOnDestroy(): void {
    this.destroy$.next(true);
    // Unsubscribe from the subject
    this.destroy$.unsubscribe();
  }
}
